/**
 * Wagamour general referral contract (Slice 2.12F).
 *
 * Invite a Dog Parent is NOT Connect our Dogs:
 * - no PlayConnectionToken
 * - no pending Play relationship
 * - no Dog Card or dog-specific URL payload
 *
 * Production referral attribution backend does not exist yet — see
 * WAGAMOUR_INVITE_PRODUCTION_WORK_REMAINING.
 */

export const WAGAMOUR_INVITE_SHARE_TEXT =
  'Join me on Wagamour — a smarter way to care for and connect around your dog.'

/** Canonical production invite destination until a dedicated /invite route ships. */
export const WAGAMOUR_CANONICAL_INVITE_URL = 'https://wagamour.com'

export const WAGAMOUR_INVITE_PREVIEW_PATH = '/dev-preview/play-integrated/invite'

export type WagamourInviteSharePayload = {
  title: string
  text: string
  url: string
}

export const WAGAMOUR_INVITE_PRODUCTION_WORK_REMAINING = [
  'Public /invite (or equivalent) landing page on wagamour.com with install + sign-in entry',
  'Optional referral attribution service (opaque ref codes — no raw user or dog IDs in URLs)',
  'Native share adapter wiring for Capacitor (Phase 2.14)',
] as const

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/$/, '')
}

/**
 * Resolves the invite URL for the current environment.
 * Local integrated preview uses a dev-only landing route; production uses the apex site.
 */
export function resolveWagamourInviteUrl(origin?: string | null): string {
  const base =
    origin?.trim() ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')

  try {
    const { hostname } = new URL(base)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${normalizeOrigin(base)}${WAGAMOUR_INVITE_PREVIEW_PATH}`
    }
  } catch {
    // fall through to canonical
  }

  return WAGAMOUR_CANONICAL_INVITE_URL
}

export function buildWagamourInviteSharePayload(
  origin?: string | null
): WagamourInviteSharePayload {
  const url = resolveWagamourInviteUrl(origin)
  return {
    title: 'Invite a Dog Parent',
    text: WAGAMOUR_INVITE_SHARE_TEXT,
    url,
  }
}

/** Ensures referral URLs never carry Connect tokens or private identifiers. */
export function wagamourInviteUrlIsSafeForReferral(url: string): boolean {
  if (!url.trim()) return false
  if (/\/play\/connect\//i.test(url)) return false
  if (/playconnectiontoken|resumeRef|bearer/i.test(url)) return false
  if (/[?&](user|dog|email|token|uid|guardian)=/i.test(url)) return false
  return true
}

export function isWagamourInvitePreviewLandingUrl(url: string): boolean {
  return url.includes(WAGAMOUR_INVITE_PREVIEW_PATH)
}
